import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import ContentCard from '@/components/base/ContentCard';
import Pagination from '@/components/base/Pagination';
import SectionHeading from '@/components/base/SectionHeading';
import FilterBar from './components/FilterBar';
import { articles } from '@/mocks/articles';
import { getCategoryByKey, getHotArticles, getCategoryName } from '@/utils/content';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 20;

const DAY = 24 * 60 * 60 * 1000;

function withinRange(date: string, range: string): boolean {
  if (range === 'all') return true;
  const diff = Date.now() - new Date(date).getTime();
  if (diff < 0) return true;
  if (range === '7d') return diff <= 7 * DAY;
  if (range === '30d') return diff <= 30 * DAY;
  return true;
}

export default function Category() {
  const { categoryName = '' } = useParams();
  const category = getCategoryByKey(categoryName);

  usePageMeta(
    `${category ? category.name : '分类'} - 鲁啊鲁多元内容平台`,
    category
      ? `${category.desc}浏览鲁啊鲁${category.name}频道的最新资讯与编辑精选内容。`
      : '浏览鲁啊鲁分类频道的最新资讯。',
  );

  const [timeRange, setTimeRange] = useState('all');
  const [sort, setSort] = useState('latest');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  const baseList = useMemo(
    () => articles.filter((item) => item.category === categoryName),
    [categoryName],
  );

  const availableTags = useMemo(
    () => Array.from(new Set(baseList.flatMap((item) => item.tags))),
    [baseList],
  );

  const filtered = useMemo(() => {
    let list = baseList.filter((item) => withinRange(item.date, timeRange));
    if (selectedTags.length > 0) {
      list = list.filter((item) => item.tags.some((tag) => selectedTags.includes(tag)));
    }
    const sorted = [...list];
    if (sort === 'latest') {
      sorted.sort((a, b) => b.date.localeCompare(a.date));
    } else {
      sorted.sort((a, b) => b.views - a.views);
    }
    return sorted;
  }, [baseList, timeRange, sort, selectedTags]);

  useEffect(() => {
    setPage(1);
  }, [timeRange, sort, selectedTags, categoryName]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleToggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((item) => item !== tag) : [...prev, tag],
    );
  };

  const handleReset = () => {
    setTimeRange('all');
    setSort('latest');
    setSelectedTags([]);
  };

  const recommended = getHotArticles(8)
    .filter((item) => item.category !== categoryName)
    .slice(0, 4);

  if (!category) {
    return (
      <div className="w-full px-4 md:px-8 py-16 text-center">
        <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
          <i className="ri-error-warning-line text-3xl"></i>
        </span>
        <h1 className="text-xl font-bold text-foreground-950">未找到该分类</h1>
        <p className="mt-2 text-sm text-foreground-500">你访问的分类不存在或已下线。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: '分类', to: '/' },
          { label: category.name },
        ]}
      />

      <div className="mt-5 rounded-lg bg-primary-50 border border-primary-100 px-6 py-7 md:px-9 md:py-9">
        <div className="flex items-center gap-4">
          <span className="w-14 h-14 shrink-0 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className={`${category.icon} text-2xl`}></i>
          </span>
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              {category.name}
            </h1>
            <p className="mt-1.5 text-sm text-foreground-600">{category.desc}</p>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <FilterBar
          timeRange={timeRange}
          onTimeRange={setTimeRange}
          sort={sort}
          onSort={setSort}
          tags={availableTags}
          selectedTags={selectedTags}
          onToggleTag={handleToggleTag}
          onReset={handleReset}
        />
      </div>

      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-foreground-500">
          共找到 <span className="font-semibold text-foreground-900">{filtered.length}</span> 篇内容
        </p>
        <span className="text-xs text-foreground-400">每页 {PAGE_SIZE} 条</span>
      </div>

      {paged.length > 0 ? (
        <>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {paged.map((article) => (
              <ContentCard key={article.id} article={article} />
            ))}
          </div>
          <Pagination current={page} total={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="mt-6 rounded-lg border border-dashed border-background-300 py-16 text-center">
          <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-inbox-line text-3xl"></i>
          </span>
          <h2 className="text-lg font-bold text-foreground-900">没有符合条件的内容</h2>
          <p className="mt-2 text-sm text-foreground-500">
            试着放宽发布时间或标签筛选，说不定会有所发现。
          </p>
          <button
            type="button"
            onClick={handleReset}
            className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line"></i>
            重置筛选条件
          </button>
        </div>
      )}

      {recommended.length > 0 ? (
        <section className="mt-14">
          <SectionHeading
            title="你可能会感兴趣"
            subtitle={`${getCategoryName(recommended[0]?.category ?? '')} 等领域的近期热门`}
            icon="ri-lightbulb-line"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {recommended.map((article) => (
              <ContentCard key={article.id} article={article} />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}